import { supabase } from './supabase';

/**
 * Cola de registros que no se pudieron subir por falta de internet.
 *
 * Por qué existe: la app se usa a las 3 de la mañana, con una mano, a
 * veces en un cuarto donde el wifi no llega. Sin esto, anotar un pañal
 * sin señal simplemente no guardaba nada — y ese dato se toma y se
 * olvida en un minuto. Es el peor momento posible para perder algo.
 *
 * Cómo funciona: si la subida falla por red, el registro se guarda acá
 * y la pantalla lo muestra igual (con marca de "pendiente"). Cuando
 * vuelve la conexión se sube solo.
 *
 * Se guarda en el almacenamiento del navegador y no en memoria a
 * propósito: si el celular cierra la app para liberar memoria —cosa
 * normal cuando está bloqueado toda la noche— lo pendiente sigue ahí al
 * volver a abrirla.
 */

const CLAVE = 'sos_padres_cola_offline';

/** Tras 5 intentos fallidos se deja de reintentar: si no subió en cinco
 *  vueltas, el problema no es la señal y reintentar para siempre solo
 *  gasta batería y datos. */
const MAX_INTENTOS = 5;

export interface Pendiente {
  /** Id temporal. Empieza con `pendiente-` para que la pantalla pueda
   *  distinguirlo de un registro ya guardado. */
  id: string;
  tabla: string;
  datos: Record<string, unknown>;
  creado: string;
  intentos: number;
}

type Oyente = (cantidad: number) => void;
const oyentes = new Set<Oyente>();

function leer(): Pendiente[] {
  try {
    const crudo = localStorage.getItem(CLAVE);
    return crudo ? (JSON.parse(crudo) as Pendiente[]) : [];
  } catch {
    // Si el contenido quedó corrupto es mejor empezar de cero que
    // dejar la app rota para siempre en cada arranque.
    return [];
  }
}

function guardar(items: Pendiente[]) {
  try {
    localStorage.setItem(CLAVE, JSON.stringify(items));
  } catch (error) {
    console.error('No se pudo guardar la cola local:', error);
  }
  oyentes.forEach((f) => f(items.length));
}

export function pendientes(): Pendiente[] {
  return leer();
}

export function cantidadPendiente(): number {
  return leer().length;
}

/** Avisa cuando cambia la cantidad de pendientes, para el indicador. */
export function alCambiar(oyente: Oyente): () => void {
  oyentes.add(oyente);
  return () => oyentes.delete(oyente);
}

/**
 * ¿Este fallo es por falta de internet, o es un error de verdad?
 *
 * Importa distinguirlo: un problema de red se reintenta, pero un dato
 * inválido o un permiso denegado no se va a arreglar solo. Encolar esos
 * dejaría basura reintentándose para siempre.
 */
export function esFalloDeRed(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (!error) return false;

  const e = error as { message?: string; code?: string; name?: string };

  // Los errores de Postgres traen código (23505, 42501…). Ésos son
  // reales: el registro está mal o no hay permiso.
  if (e.code && /^[0-9A-Z]{5}$/.test(e.code)) return false;

  const texto = `${e.name ?? ''} ${e.message ?? ''}`.toLowerCase();
  return (
    texto.includes('failed to fetch') ||
    texto.includes('networkerror') ||
    texto.includes('network request failed') ||
    texto.includes('load failed') ||
    texto.includes('timeout')
  );
}

/**
 * Guarda un registro para subirlo después.
 * Devuelve el objeto tal como debe mostrarse en pantalla mientras tanto.
 */
export function encolar<T = Record<string, unknown>>(
  tabla: string,
  datos: Record<string, unknown>,
): T {
  const item: Pendiente = {
    id: `pendiente-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    tabla,
    datos,
    creado: new Date().toISOString(),
    intentos: 0,
  };

  guardar([...leer(), item]);

  // Se devuelve con la misma forma que tendría el registro real, para
  // que la pantalla lo pinte sin tener que saber que está pendiente.
  return {
    ...datos,
    id: item.id,
    created_at: item.creado,
    _pendiente: true,
  } as T;
}

let sincronizando = false;

/**
 * Intenta subir todo lo pendiente. Devuelve cuántos logró subir.
 *
 * Se protege contra corridas simultáneas: sin eso, volver la conexión
 * justo cuando el usuario abre la app dispara dos sincronizaciones y
 * cada registro se sube dos veces.
 */
export async function sincronizar(): Promise<number> {
  if (sincronizando) return 0;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 0;

  const cola = leer();
  if (cola.length === 0) return 0;

  sincronizando = true;
  let subidos = 0;
  const quedan: Pendiente[] = [];
  // Se recorre por índice para poder conservar TODO lo que no se llegó
  // a procesar si algo revienta a mitad del bucle. Con un `for...of` y
  // un `finally` que guarda, una excepción inesperada se llevaría por
  // delante los registros que todavía no se habían intentado.
  let i = 0;

  try {
    for (; i < cola.length; i++) {
      const item = cola[i];
      const { error } = await supabase.from(item.tabla).insert([item.datos]);

      if (!error) {
        subidos++;
        continue;
      }

      if (esFalloDeRed(error)) {
        // Sigue sin haber señal: se conserva sin gastar un intento y se
        // corta, porque los demás van a fallar igual.
        break;
      }

      // Error real (dato inválido, permiso denegado). Se reintenta unas
      // pocas veces por si fue algo pasajero, y luego se descarta.
      const intentos = item.intentos + 1;
      if (intentos < MAX_INTENTOS) {
        quedan.push({ ...item, intentos });
      } else {
        console.error(
          `Se descarta un registro de ${item.tabla} tras ${MAX_INTENTOS} intentos:`,
          error,
        );
      }
    }
  } catch (error) {
    console.error('La sincronización se cortó:', error);
  } finally {
    // Todo lo que quedó desde `i` en adelante no se llegó a intentar
    // (o se cortó por falta de señal): se conserva intacto.
    guardar([...quedan, ...cola.slice(i)]);
    sincronizando = false;
  }

  return subidos;
}

/**
 * Arranca la sincronización automática.
 *
 * Se llama una vez al iniciar la app. Sincroniza al volver la conexión
 * y también al traer la app al frente: en el celular, "volver la
 * conexión" muchas veces ocurre con la pantalla apagada y el evento
 * `online` no siempre llega.
 */
export function iniciarSincronizacionAutomatica() {
  if (typeof window === 'undefined') return;

  const intentar = () => { void sincronizar(); };

  window.addEventListener('online', intentar);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') intentar();
  });

  intentar();
}
